/**
 * Cache Utilities for localStorage management
 *
 * Provides utilities for managing localStorage with automatic Last Recently Used (LRU) eviction
 * when quota is exceeded. Includes hash-based key generation to reduce key length.
 */

import { logger } from 'presentation/components/KSExport/reportNTParser/utils';

/**
 * Simple hash function to generate short, consistent keys from long strings
 * Uses a basic hash algorithm suitable for cache keys (not cryptographic)
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to base36 for shorter string representation
  return Math.abs(hash).toString(36);
}

/**
 * Generate a short cache key using hash
 */
export function generateCacheKey(prefix: string, identifier: string): string {
  const hash = simpleHash(identifier);
  return `${prefix}-${hash}`;
}

/**
 * Interface for cache entry metadata
 */
interface CacheEntry {
  key: string;
  timestamp: number;
  size: number;
}

/**
 * Get all cache entries with a specific prefix, sorted by timestamp (oldest first)
 */
function getCacheEntriesByPrefix(prefix: string): CacheEntry[] {
  const entries: CacheEntry[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      const value = localStorage.getItem(key);
      if (value) {
        // Try to get timestamp from companion timestamp key
        const timestampKey = key.replace('-data-', '-timestamp-');
        const timestampStr = localStorage.getItem(timestampKey);
        let timestamp = timestampStr ? parseInt(timestampStr, 10) : 0;
        // If no companion timestamp, try to read embedded timestamp from JSON payload
        if (!timestamp) {
          try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === 'object' && typeof parsed.timestamp === 'number') {
              timestamp = parsed.timestamp;
            }
          } catch {
            // ignore JSON parse errors
          }
        }

        entries.push({
          key,
          timestamp,
          size: value.length,
        });
      }
    }
  }

  // Sort by timestamp (oldest first)
  return entries.sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Delete the oldest cache entry with the given prefix
 * Returns true if an entry was deleted, false if no entries found
 */
function deleteOldestCacheEntry(prefix: string): boolean {
  const entries = getCacheEntriesByPrefix(prefix);

  if (entries.length === 0) {
    return false;
  }

  const oldest = entries[0];

  // Delete both data and timestamp keys, regardless of which was matched
  const key = oldest.key;
  const isDataKey = key.includes('-data-');
  const isTimestampKey = key.includes('-timestamp-');
  const dataKey = isDataKey ? key : isTimestampKey ? key.replace('-timestamp-', '-data-') : key;
  const timestampKey = isTimestampKey ? key : isDataKey ? key.replace('-data-', '-timestamp-') : '';

  // Always remove the primary key
  localStorage.removeItem(key);
  // Also remove its counterpart when identifiable
  if (timestampKey) {
    localStorage.removeItem(timestampKey);
  } else if (dataKey !== key) {
    // If we derived a dataKey from a timestamp key, remove it as well
    localStorage.removeItem(dataKey);
  }

  logger.msg(
    `[CacheUtils] Evicted oldest cache entry pair: ${dataKey}${timestampKey ? ` + ${timestampKey}` : ''} (age: ${
      Date.now() - oldest.timestamp
    }ms)`
  );

  return true;
}

/**
 * Set item in localStorage with automatic LRU eviction on quota exceeded
 *
 * @param key The localStorage key
 * @param value The value to store (will be stringified if object)
 * @param cachePrefix The prefix to use for finding old entries to evict
 * @param maxRetries Maximum number of eviction attempts before giving up
 * @returns true if successful, false if failed after all retries
 */
export function setItemWithEviction(key: string, value: string, cachePrefix: string, maxRetries: number = 10): boolean {
  let attempts = 0;

  while (attempts <= maxRetries) {
    try {
      localStorage.setItem(key, value);

      if (attempts > 0) {
        // eslint-disable-next-line no-console
        console.log(`[CacheUtils] Successfully saved after ${attempts} eviction(s)`);
      }

      return true;
    } catch (error) {
      // Check if it's a quota exceeded error
      const isQuotaError =
        error instanceof DOMException &&
        (error.name === 'QuotaExceededError' ||
          error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
          error.code === 22 || // Legacy code
          error.code === 1014); // Firefox

      if (!isQuotaError) {
        // Different error, log and return false
        console.error('[CacheUtils] localStorage error (not quota):', error);
        return false;
      }

      // Quota exceeded - try to evict oldest entry
      console.warn(
        `[CacheUtils] localStorage quota exceeded, attempting eviction (attempt ${attempts + 1}/${maxRetries + 1})`
      );

      const evicted = deleteOldestCacheEntry(cachePrefix);

      if (!evicted) {
        // No entries to evict, cannot proceed
        console.error('[CacheUtils] No cache entries to evict, cannot save');
        return false;
      }

      attempts++;
    }
  }

  console.error(`[CacheUtils] Failed to save after ${maxRetries} eviction attempts`);
  return false;
}

/**
 * Clear all cache entries with a specific prefix
 *
 * @param prefix The cache key prefix to clear
 * @returns Number of entries cleared
 */
export function clearCacheByPrefix(prefix: string): number {
  const keysToDelete: string[] = [];

  // Collect all keys with the prefix
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      keysToDelete.push(key);
    }
  }

  // Delete all collected keys
  keysToDelete.forEach(key => localStorage.removeItem(key));

  if (keysToDelete.length > 0) {
    logger.msg(`[CacheUtils] Cleared ${keysToDelete.length} cache entries with prefix: ${prefix}`);
  }

  return keysToDelete.length;
}

/**
 * Get cache statistics for a specific prefix
 *
 * @param prefix The cache key prefix to analyze
 * @returns Statistics about the cache
 */
export function getCacheStats(prefix: string): {
  count: number;
  totalSize: number;
  oldestTimestamp: number | null;
  newestTimestamp: number | null;
} {
  const entries = getCacheEntriesByPrefix(prefix);

  if (entries.length === 0) {
    return {
      count: 0,
      totalSize: 0,
      oldestTimestamp: null,
      newestTimestamp: null,
    };
  }

  const totalSize = entries.reduce((sum, entry) => sum + entry.size, 0);
  const timestamps = entries.map(e => e.timestamp).filter(t => t > 0);

  return {
    count: entries.length,
    totalSize,
    oldestTimestamp: timestamps.length > 0 ? Math.min(...timestamps) : null,
    newestTimestamp: timestamps.length > 0 ? Math.max(...timestamps) : null,
  };
}

/**
 * Get a safe wrapper for localStorage.getItem
 * Returns null if any error occurs
 */
export function getItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error('[CacheUtils] Error reading from localStorage:', error);
    return null;
  }
}

/**
 * Get a safe wrapper for localStorage.setItem (without eviction)
 * Returns true if successful, false otherwise
 */
export function setItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error('[CacheUtils] Error writing to localStorage:', error);
    return false;
  }
}

/**
 * Get a safe wrapper for localStorage.removeItem
 * Returns true if successful, false otherwise
 */
export function removeItem(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('[CacheUtils] Error removing from localStorage:', error);
    return false;
  }
}

/**
 * Remove a cache entry and its counterpart timestamp/data key if present
 */
function removeCacheEntryPair(key: string): number {
  let removed = 0;
  try {
    localStorage.removeItem(key);
    removed++;
  } catch {
    // Ignore localStorage errors
  }

  const isDataKey = key.includes('-data-');
  const isTimestampKey = key.includes('-timestamp-');
  const counterpart = isDataKey
    ? key.replace('-data-', '-timestamp-')
    : isTimestampKey
      ? key.replace('-timestamp-', '-data-')
      : '';

  if (counterpart) {
    try {
      localStorage.removeItem(counterpart);
      removed++;
    } catch {
      // Ignore localStorage errors
    }
  }

  return removed;
}

/**
 * Purge legacy AI Analysis cache entries that used long, non-hashed keys
 * Matches keys that start with 'ai-analysis-summary-data-' and include '-type=' and '-timeframe='
 */
export function purgeLegacyAIAnalysisCache(): number {
  const LEGACY_PREFIX = 'ai-analysis-summary-data-';
  let removed = 0;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (key.startsWith(LEGACY_PREFIX) && key.includes('-type=') && key.includes('-timeframe=')) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(k => {
    removed += removeCacheEntryPair(k);
  });

  if (removed > 0) {
    logger.msg(`[CacheUtils] Purged ${removed} legacy AI Analysis cache item(s)`);
  }

  return removed;
}

/**
 * Prune expired cache entries for a given prefix using timestamps (embedded or companion)
 */
export function pruneExpiredCache(prefix: string, expiryMs: number): number {
  const now = Date.now();
  const entries = getCacheEntriesByPrefix(prefix);
  let removed = 0;

  entries.forEach(entry => {
    if (entry.timestamp && now - entry.timestamp > expiryMs) {
      removed += removeCacheEntryPair(entry.key);
    }
  });

  if (removed > 0) {
    logger.msg(`[CacheUtils] Pruned ${removed} expired cache item(s) for prefix '${prefix}'`);
  }

  return removed;
}

/**
 * Purge legacy AI Editor prompt version entries
 * Removes all old keys (long-form and hashed) except the new global key
 */
export function purgeLegacyPromptVersionCache(): number {
  const LEGACY_PREFIX = 'ai-editor-prompt-versions-';
  const NEW_GLOBAL_KEY = 'ai-editor-prompt-versions-global';
  let removed = 0;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    // Remove all old prompt version keys except the new global one
    if (key.startsWith(LEGACY_PREFIX) && key !== NEW_GLOBAL_KEY) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(k => {
    try {
      localStorage.removeItem(k);
      removed++;
    } catch {
      // Ignore localStorage errors
    }
  });

  if (removed > 0) {
    logger.msg(`[CacheUtils] Purged ${removed} legacy AI Editor prompt version cache item(s)`);
  }

  return removed;
}

/**
 * Purge legacy AI Editor response cache entries
 * Removes old long-form and individual response keys, keeps only new hashed group keys
 */
export function purgeLegacyAIEditorResponseCache(): number {
  const LEGACY_PREFIXES = ['ai-editor-internal-', 'ai-editor-external-', 'ai-editor-group-'];
  let removed = 0;

  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    // Remove old long-form keys (with reportId and params visible)
    for (const prefix of LEGACY_PREFIXES) {
      if (key.startsWith(prefix) && (key.includes('-type=') || key.includes('timeframe='))) {
        keysToRemove.push(key);
        break;
      }
    }
  }

  keysToRemove.forEach(k => {
    try {
      localStorage.removeItem(k);
      removed++;
    } catch {
      // Ignore localStorage errors
    }
  });

  if (removed > 0) {
    logger.msg(`[CacheUtils] Purged ${removed} legacy AI Editor response cache item(s)`);
  }

  return removed;
}

/**
 * Run cache housekeeping (once): purge legacy keys and prune expired entries
 */
export function runAIAnalysisCacheHousekeepingOnce(): {
  legacyAIAnalysisRemoved: number;
  legacyPromptVersionsRemoved: number;
  legacyAIEditorResponsesRemoved: number;
  expiredRemoved: number;
  ran: boolean;
} {
  const FLAG = 'ks-ai-housekeeping-v1';
  if (localStorage.getItem(FLAG)) {
    return {
      legacyAIAnalysisRemoved: 0,
      legacyPromptVersionsRemoved: 0,
      legacyAIEditorResponsesRemoved: 0,
      expiredRemoved: 0,
      ran: false,
    };
  }

  const legacyAIAnalysisRemoved = purgeLegacyAIAnalysisCache();
  const legacyPromptVersionsRemoved = purgeLegacyPromptVersionCache();
  const legacyAIEditorResponsesRemoved = purgeLegacyAIEditorResponseCache();
  const expiredRemoved = pruneExpiredCache('ai-analysis-summary-data', 2 * 24 * 60 * 60 * 1000);

  try {
    localStorage.setItem(FLAG, '1');
  } catch {
    // Ignore localStorage errors
  }

  return {
    legacyAIAnalysisRemoved,
    legacyPromptVersionsRemoved,
    legacyAIEditorResponsesRemoved,
    expiredRemoved,
    ran: true,
  };
}
